import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

export const Route = createFileRoute('/sso/admin/authenticate')({
  component: AdminPortalAuthenticatePage,
});

function AdminPortalAuthenticatePage() {
  'use no memo'; // Dynamic imports (Stytch SDK) prevent React Compiler optimization
  /* oxlint-disable react/react-compiler */
  const [error, setError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const authenticate = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token');
      const tokenType = searchParams.get('stytch_token_type');

      if (!token || tokenType !== 'multi_tenant_magic_links') {
        if (!cancelled) {
          setError(
            'Invalid or expired magic link. Please request a new one from the SSO admin page.'
          );
          setAuthenticating(false);
        }
        return;
      }

      try {
        // Dynamically import Stytch to avoid bundling it in non-SSO routes
        const { StytchB2BHeadlessClient } = await import('@stytch/vanilla-js/b2b/headless');

        // The public token is stored in localStorage by the admin page (task 9.1)
        // when getAdminPortalSession returns it
        const stytchPublicToken =
          searchParams.get('stytch_public_token') ||
          searchParams.get('public_token') ||
          window.localStorage.getItem('stytch_public_token') ||
          '';

        if (!stytchPublicToken) {
          if (!cancelled) {
            setError(
              'Stytch configuration not found. Please use the SSO admin page to request a new link.'
            );
            setAuthenticating(false);
          }
          return;
        }

        const stytchClient = new StytchB2BHeadlessClient(stytchPublicToken);

        await stytchClient.magicLinks.authenticate({
          magic_links_token: token,
          session_duration_minutes: 60,
        });

        if (!cancelled) {
          // Persist the public token so the portal page can read it
          window.localStorage.setItem('stytch_public_token', stytchPublicToken);
          // Stytch session is now stored in cookies by the SDK
          // Redirect to the portal page
          void navigate({ to: '/sso/admin/portal' });
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Authentication failed';
          setError(`Magic link authentication failed: ${message}. Please request a new link.`);
          setAuthenticating(false);
        }
      }
    };

    void authenticate();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            void navigate({ to: '/' });
          }}
        >
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  if (authenticating) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Authenticating...</Typography>
      </Box>
    );
  }

  return null;
}
